/**
 * SearchBar Component (AC-654)
 *
 * A search input component for the help documentation system with debounced
 * search, results dropdown, keyboard navigation, and accessibility features.
 *
 * Technical Design Specification:
 * - Input with search icon (MagnifyingGlassIcon)
 * - Debounced search - calls onSearch after 300ms delay
 * - Loading spinner when isSearching is true
 * - Results dropdown when results.length > 0
 * - Results show title, category badge, and description
 * - Click result calls onResultSelect(path)
 * - Clear button (XMarkIcon) when query not empty
 * - Keyboard navigation: ArrowDown/ArrowUp, Enter, Escape
 * - Full ARIA combobox pattern for accessibility
 *
 * @see AC-654 - Create SearchBar component
 * @see AC-650 - Epic: In-App Documentation System
 */

import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DocSearchResult } from '../../types/help';

/**
 * Props for the SearchBar component.
 */
export interface SearchBarProps {
  /** Callback when search query changes (debounced) */
  onSearch: (query: string) => void;
  /** Callback when a result is selected */
  onResultSelect: (path: string) => void;
  /** Search results to display */
  results: DocSearchResult[];
  /** Whether a search is in progress */
  isSearching: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
}

/**
 * Category label mapping for search result badges.
 */
const categoryLabels: Record<string, string> = {
  'getting-started': 'Getting Started',
  'features': 'Features',
  'how-to': 'How-To',
  'troubleshooting': 'Troubleshooting',
};

/**
 * Extract category from a doc path (e.g., "getting-started/welcome.md" → "getting-started").
 */
const getCategoryFromPath = (path: string): string | null => {
  const parts = path.split('/');
  return parts.length >= 2 ? parts[0] : null;
};

/**
 * SearchBar - Search input with debounced search, results dropdown, and keyboard navigation.
 */
const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onResultSelect,
  results,
  isSearching,
  placeholder = 'Search documentation...',
}) => {
  // Input value state
  const [query, setQuery] = useState('');
  // Currently highlighted result index (-1 means none selected)
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Whether the dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Generate unique IDs for accessibility
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  // Determine if dropdown should be visible
  const hasQuery = query.trim().length > 0;
  const hasResults = results.length > 0;
  // Show dropdown if: (we have results AND dropdown is open) OR (dropdown is open AND user has typed a query AND search completed with no results)
  // Note: isDropdownOpen is set to false on clear/escape, so results won't show after clearing
  const showResultsDropdown = hasResults && isDropdownOpen;
  const showNoResults = isDropdownOpen && hasQuery && !hasResults && !isSearching;

  /**
   * Clear the search input and close dropdown.
   */
  const clearSearch = useCallback(() => {
    setQuery('');
    setHighlightedIndex(-1);
    setIsDropdownOpen(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    onSearch('');
    inputRef.current?.focus();
  }, [onSearch]);

  /**
   * Handle input change with debounced search.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setHighlightedIndex(-1);
    setIsDropdownOpen(true);

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only search if not empty/whitespace
    const trimmed = value.trim();
    if (trimmed) {
      debounceTimerRef.current = setTimeout(() => {
        onSearch(value);
      }, 300);
    }
  };

  /**
   * Handle keyboard navigation.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (hasResults) {
          setHighlightedIndex((prev) => {
            const next = prev + 1;
            return next >= results.length ? 0 : next;
          });
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (hasResults) {
          setHighlightedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? results.length - 1 : next;
          });
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          const selected = results[highlightedIndex];
          onResultSelect(selected.item.path);
          setQuery('');
          setHighlightedIndex(-1);
          setIsDropdownOpen(false);
        }
        break;

      case 'Escape':
        e.preventDefault();
        clearSearch();
        break;
    }
  };

  /**
   * Handle clicking on a result.
   */
  const handleResultClick = (path: string) => {
    onResultSelect(path);
    setQuery('');
    setHighlightedIndex(-1);
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  /**
   * Open dropdown when results are provided.
   */
  useEffect(() => {
    if (results.length > 0) {
      setIsDropdownOpen(true);
    }
  }, [results]);

  /**
   * Handle clicking outside to close dropdown.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Cleanup debounce timer on unmount.
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Get the ID for a result option element.
   */
  const getOptionId = (index: number) => `${baseId}-option-${index}`;

  /**
   * Get the active descendant ID (currently highlighted option).
   */
  const getActiveDescendant = () => {
    if (highlightedIndex >= 0 && highlightedIndex < results.length) {
      return getOptionId(highlightedIndex);
    }
    return undefined;
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search Input Container */}
      <div className="relative">
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon
            data-testid="search-icon"
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Search documentation"
          aria-haspopup="listbox"
          aria-expanded={showResultsDropdown}
          aria-controls={listboxId}
          aria-activedescendant={getActiveDescendant()}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white leading-5 bg-white dark:bg-cap-dark text-gray-900 dark:text-warm-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />

        {/* Loading Spinner */}
        {isSearching && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <div
              data-testid="search-spinner"
              aria-hidden="true"
              className="animate-spin h-4 w-4 border-2 border-gray-300 dark:border-gray-600 border-t-indigo-600 rounded-full"
            />
          </div>
        )}

        {/* Clear Button */}
        {hasQuery && (
          <button
            type="button"
            data-testid="search-clear-button"
            aria-label="Clear search"
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {showResultsDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Search results"
          className="absolute z-10 mt-1 w-full bg-white dark:bg-cap-dark shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
        >
          {results.map((result, index) => {
            const isHighlighted = index === highlightedIndex;
            const optionId = getOptionId(index);

            return (
              <li
                key={result.item.id}
                id={optionId}
                role="option"
                aria-selected={isHighlighted}
                onClick={() => handleResultClick(result.item.path)}
                className={`cursor-pointer select-none relative py-2 px-3 ${
                  isHighlighted
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200'
                    : 'text-gray-900 dark:text-warm-white hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {/* Title and Category Badge */}
                <span className="flex items-center gap-2">
                  <span className="truncate font-medium">
                    {result.item.title}
                  </span>
                  {(() => {
                    const cat = getCategoryFromPath(result.item.path);
                    const label = cat ? categoryLabels[cat] : null;
                    return label ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0">
                        {label}
                      </span>
                    ) : null;
                  })()}
                </span>

                {/* Description */}
                {result.item.description && (
                  <span className="block truncate text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {result.item.description}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* No Results Message */}
      {showNoResults && (
        <div
          role="status"
          aria-live="polite"
          className="absolute z-10 mt-1 w-full bg-white dark:bg-cap-dark shadow-lg rounded-md py-3 px-4 text-sm ring-1 ring-black ring-opacity-5"
        >
          <p className="text-gray-700 dark:text-gray-300 font-medium">
            No results found for "{query.trim()}"
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
            Try different keywords or browse topics below
          </p>
        </div>
      )}

      {/* Live Region for Loading State Announcements */}
      {isSearching && (
        <div
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          Searching...
        </div>
      )}
    </div>
  );
};

export default SearchBar;
